import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';
import { IAppConfig } from '../types/appConfiguration/appConfigTypes';
import {
    APP_CONFIG_INITIAL_STATE,
    APP_KEY,
} from '../utils/appConfiguration/appConfigurationUtils';
import { useNotification } from './NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import { buildEndPoint } from '../utils/odataEndPoint';
import { readOnlyEntities } from '../utils/commonVariables/entityKeys';
import api from '../services/api';
import { IApplicationConfiguration } from '../types/entities/IApplicationConfiguration';

export interface AppConfigurationContextType {
    appConfiguration: IAppConfig;
    getAppConfiguration: () => Promise<IApplicationConfiguration[] | undefined>;
    updateAppConfiguration: (appConfig: Partial<IAppConfig>) => void;
}

const AppConfigurationContext =
    createContext<AppConfigurationContextType | null>(null);

export const AppConfigurationProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { setErrorMessage } = useNotification();
    const [appConfiguration, setAppConfiguration] = useState<IAppConfig>(
        APP_CONFIG_INITIAL_STATE
    );

    const getAppConfiguration = useCallback(async () => {
        try {
            const endpoint = buildEndPoint({
                entityName: readOnlyEntities.ApplicationConfiguration,
                query: {
                    select: [
                        'Value',
                        `${readOnlyEntities.ApplicationConfigurationKey}/Key`,
                        `${readOnlyEntities.ApplicationConfigurationKey}/DefaultValue`,
                    ],
                    expand: [readOnlyEntities.ApplicationConfigurationKey],
                    filter: [
                        `${readOnlyEntities.ApplicationConfigurationKey}/Enabled`,
                        `${readOnlyEntities.ApplicationConfigurationKey}/ForClient`,
                        `${readOnlyEntities.ApplicationConfigurationKey}/${readOnlyEntities.Application}s/AppToken eq '${APP_KEY}'`,
                    ],
                },
            });

            const { data } = await api.get(endpoint);
            return data.value;
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        }
    }, [setErrorMessage]);

    const updateAppConfiguration = (appConfig: Partial<IAppConfig>) => {
        setAppConfiguration(prev => ({ ...prev, ...appConfig }));
    };

    return (
        <AppConfigurationContext.Provider
            value={{
                appConfiguration,
                getAppConfiguration,
                updateAppConfiguration,
            }}
        >
            {children}
        </AppConfigurationContext.Provider>
    );
};

export const useAppConfiguration = () => {
    const context = useContext(AppConfigurationContext);
    if (!context) {
        throw new Error(
            'useAppConfiguration must be used within an AppConfigurationProvider'
        );
    }

    const { appConfiguration, getAppConfiguration, updateAppConfiguration } =
        context;

    return {
        appConfiguration,
        updateAppConfiguration,
        getAppConfiguration,
    };
};

export const useAppConfigurationSections = () => {
    const { appConfiguration } = useAppConfiguration();

    const zinantisPagesVisible = Object.values(
        appConfiguration.sections.zinantis.pages
    ).some(Boolean);
    const corePagesVisible = Object.values(
        appConfiguration.sections.core.pages
    ).some(Boolean);
    const profilePagesVisible = Object.values(
        appConfiguration.sections.profile.pages
    ).some(Boolean);
    const zitoneoPagesVisible = Object.values(
        appConfiguration.sections.zitoneo.pages
    ).some(Boolean);

    const isCoreSectionVisible =
        appConfiguration.sections.core.isVisible && corePagesVisible;
    const isZinantisSectionVisible =
        appConfiguration.sections.zinantis.isVisible && zinantisPagesVisible;
    const isProfileSectionVisible =
        appConfiguration.sections.profile.isVisible && profilePagesVisible;
    const isZitoneoSectionVisible =
        appConfiguration.sections.zitoneo.isVisible && zitoneoPagesVisible;

    return {
        ...appConfiguration.sections,
        isCoreSectionVisible,
        isZinantisSectionVisible,
        isProfileSectionVisible,
        isZitoneoSectionVisible,
    };
};

export const useAppConfigurationFeatures = () => {
    const { appConfiguration } = useAppConfiguration();
    return {
        ...appConfiguration.features,
    };
};
